// src/auth/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token'));

  // Extract token from URL fragment (after Auth0 callback)
  const extractTokenFromUrl = () => {
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('access_token');
      const tokenType = params.get('token_type');
      
      if (token && tokenType === 'Bearer') {
        // Store token
        localStorage.setItem('access_token', token);
        setAccessToken(token);
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        
        return token;
      }
    }
    return null;
  };

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  };

  // Clear authentication data
  const clearAuth = () => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem('access_token');
  };

  // Authenticated fetch wrapper
  const authenticatedFetch = async (url, options = {}) => {
    const token = accessToken || localStorage.getItem('access_token');
    
    if (!token || isTokenExpired(token)) {
      clearAuth();
      throw new Error('No valid authentication token');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      clearAuth();
      throw new Error('Authentication failed');
    }

    if (response.status === 403) {
      clearAuth();
      throw new Error('Access denied - not whitelisted for admin access');
    }

    return response;
  };

  // Get user info from token
  const getUserFromToken = (token) => {
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
        preferences: payload.preferences || {},
        group: payload.group,
        clinic: payload.clinic,
        subscription: payload.subscription,
        flics: payload.flics
      };
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  };

  // Check authentication status
  const checkAuth = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check for URL error parameters first
      const urlParams = new URLSearchParams(window.location.search);
      const urlError = urlParams.get('error');
      
      if (urlError === 'not_whitelisted') {
        setError('Access denied: Your email is not whitelisted for admin access');
        clearAuth();
        return;
      }
      
      if (urlError === 'auth_error') {
        setError('Authentication failed. Please try again.');
        clearAuth();
        return;
      }

      // First, check if there's a token in the URL (from Auth0 callback)
      let token = extractTokenFromUrl();
      
      // If no token in URL, check localStorage
      if (!token) {
        token = localStorage.getItem('access_token');
      }

      if (!token || isTokenExpired(token)) {
        clearAuth();
        return;
      }

      // Get user info from token
      const userFromToken = getUserFromToken(token);
      if (userFromToken) {
        setUser(userFromToken);
        setAccessToken(token);
      } else {
        clearAuth();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setError(error.message);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  // Login - redirect to backend login endpoint with admin return URL
  const login = () => {
    const backendUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    const loginUrl = new URL(`${backendUrl}/api/auth/login`);

    const adminUrl = process.env.REACT_APP_ADMIN_URL || window.location.origin;
    loginUrl.searchParams.set('returnTo', `${adminUrl}/dashboard`);
    
    window.location.href = loginUrl.toString();
  };

  // Logout
  const logout = async () => {
    try {
      clearAuth();
      
      // Redirect to backend logout endpoint which handles Auth0 logout
      window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/auth/logout`;
    } catch (error) {
      console.error('Logout failed:', error);
      setError(error.message);
    }
  };

  // Check auth on mount and when URL changes
  useEffect(() => {
    checkAuth();
  }, []);

  // Set up token expiration checking
  useEffect(() => {
    if (accessToken && !isTokenExpired(accessToken)) {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const timeUntilExpiration = expirationTime - Date.now();
      
      if (timeUntilExpiration > 0) {
        const timeout = setTimeout(() => {
          console.log('Token expired, clearing auth');
          clearAuth();
        }, timeUntilExpiration);
        
        return () => clearTimeout(timeout);
      } else {
        clearAuth();
      }
    }
  }, [accessToken]);

  const value = {
    user,
    isLoading,
    error,
    login,
    logout,
    authenticatedFetch,
    isAuthenticated: !!user && !!accessToken && !isTokenExpired(accessToken),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 